
import { CommonModule, DatePipe } from "@angular/common";
import { Component, inject, OnDestroy, OnInit, signal } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { RouterLink, RouterLinkActive } from "@angular/router";
import { TuiPortals } from "@taiga-ui/cdk";
import {
  TuiAlertService,
  TuiAppearance,
  TuiButton,
  TuiDataList,
  TuiDropdown,
  TuiDropdownAlign,
  TuiIcon,
  TuiSizeL,
  TuiSizeS,
  TuiTextfield,
} from "@taiga-ui/core";
import {
  TuiBadge,
  TuiBadgeNotification,
  TuiChevron,
  TuiDataListDropdownManager,
  TuiDataListWrapper,
  TuiTabs,
} from "@taiga-ui/kit";
import {
  TuiNavigation,
} from "@taiga-ui/layout";
import { finalize, Subscription } from "rxjs";
import { AdminService } from "../../core/services/admin.service";
import { ClientService } from "../../core/services/client.service";
import { TuiTable } from "@taiga-ui/addon-table";
import { AuthService } from "../../core/services/auth.service";
import { NotificationService } from "../../core/services/notification.service";
import { SessionExpiryService } from "../../core/services/session-expiry.service";
import { formatClientIdentifier } from "../../shared/utils/client-identifier";


const ICON =
  "data:image/svg+xml,%0A%3Csvg width='32' height='32' viewBox='0 0 32 32' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='32' height='32' rx='8' fill='url(%23paint0_linear_2036_35276)'/%3E%3Cmask id='mask0_2036_35276' style='mask-type:alpha' maskUnits='userSpaceOnUse' x='6' y='5' width='20' height='21'%3E%3Cpath d='M18.2399 9.36607C21.1347 10.1198 24.1992 9.8808 26 7.4922C26 7.4922 21.5645 5 16.4267 5C11.2888 5 5.36726 8.69838 6.05472 16.6053C6.38707 20.4279 6.65839 23.7948 6.65839 23.7948C8.53323 22.1406 9.03427 19.4433 8.97983 16.9435C8.93228 14.7598 9.55448 12.1668 12.1847 10.4112C14.376 8.94865 16.4651 8.90397 18.2399 9.36607Z' fill='url(%23paint1_linear_2036_35276)'/%3E%3Cpath d='M11.3171 20.2647C9.8683 17.1579 10.7756 11.0789 16.4267 11.0789C20.4829 11.0789 23.1891 12.8651 22.9447 18.9072C22.9177 19.575 22.9904 20.2455 23.2203 20.873C23.7584 22.3414 24.7159 24.8946 24.7159 24.8946C23.6673 24.5452 22.8325 23.7408 22.4445 22.7058L21.4002 19.921L21.2662 19.3848C21.0202 18.4008 20.136 17.7104 19.1217 17.7104H17.5319L17.6659 18.2466C17.9119 19.2306 18.7961 19.921 19.8104 19.921L22.0258 26H10.4754C10.7774 24.7006 12.0788 23.2368 11.3171 20.2647Z' fill='url(%23paint2_linear_2036_35276)'/%3E%3C/mask%3E%3Cg mask='url(%23mask0_2036_35276)'%3E%3Crect x='4' y='4' width='24' height='24' fill='white'/%3E%3C/g%3E%3Cdefs%3E%3ClinearGradient id='paint0_linear_2036_35276' x1='0' y1='0' x2='32' y2='32' gradientUnits='userSpaceOnUse'%3E%3Cstop stop-color='%23A681D4'/%3E%3Cstop offset='1' stop-color='%237D31D4'/%3E%3C/linearGradient%3E%3ClinearGradient id='paint1_linear_2036_35276' x1='6.0545' y1='24.3421' x2='28.8119' y2='3.82775' gradientUnits='userSpaceOnUse'%3E%3Cstop offset='0.0001' stop-opacity='0.996458'/%3E%3Cstop offset='0.317708'/%3E%3Cstop offset='1' stop-opacity='0.32'/%3E%3C/linearGradient%3E%3ClinearGradient id='paint2_linear_2036_35276' x1='6.0545' y1='24.3421' x2='28.8119' y2='3.82775' gradientUnits='userSpaceOnUse'%3E%3Cstop offset='0.0001' stop-opacity='0.996458'/%3E%3Cstop offset='0.317708'/%3E%3Cstop offset='1' stop-opacity='0.32'/%3E%3C/linearGradient%3E%3C/defs%3E%3C/svg%3E%0A";

@Component({
  standalone: true,
  imports: [
    FormsModule,
    RouterLink,
    RouterLinkActive,
    TuiAppearance,
    TuiBadge,
    TuiBadgeNotification,
    TuiButton,
    TuiChevron,
    TuiDataList,
    TuiDropdown,
    TuiIcon,
    TuiNavigation,
    TuiTabs,
    TuiTextfield,
    RouterOutlet,
    CommonModule,
    TuiDataListWrapper,
    TuiTable,
    TuiDataListDropdownManager,
    ReactiveFormsModule
  ],
  selector: "app-admin-layout",
  templateUrl: "./admin-layout.component.html",
  styleUrl: "./admin-layout.component.scss",
  providers: [DatePipe],
})
export class AdminLayoutComponent implements OnInit, OnDestroy {
  protected expanded = signal(true);
  protected open = false;
  protected switch = false;
  align: TuiDropdownAlign = 'center'; 
  loggedInUserName: string | null = null;
  loggedInUserRole: string | "Role" = "Role";
  loggedInUserID: string | null = null;
  loggedInUserImage: string | null = null;
  protected readonly routes: any = {};
  protected readonly breadcrumbs = [
    "Home",
    "Angular",
    "Repositories",
    "Taiga UI",
  ];

  // Expiring clients notification properties
  showExpiringClientsDialog = false;
  expiringClientsCount = 0;
  expiringClients: any[] = [];
  clientService = inject(ClientService);
  private notificationService = inject(NotificationService);
  private alerts = inject(TuiAlertService);
  // Expiring clients dialog is dismissible immediately.
  countdownSeconds = 0;
  private countdownInterval: any;

  // Notification bell state - polled for super admins so login alerts surface.
  notifications: any[] = [];
  unopenedNotificationCount = 0;
  notificationDropdownOpen = false;
  private notificationPollInterval: any;
  private readonly NOTIFICATION_POLL_MS = 15000;

  /**
   * Session-expired banner state. Driven by SessionExpiryService, which
   * the auth interceptor flips on when a 401/403 + refresh cascade fails.
   */
  sessionExpiredBannerVisible = false;
  private sessionExpirySub: Subscription | null = null;
  private sessionExpiry = inject(SessionExpiryService);

  constructor(
      private authService: AuthService,
      public router: Router,
      private route: ActivatedRoute,
      private datePipe: DatePipe,
      private adminService: AdminService
    ) {
  }

  ngOnInit(): void {
    this.loadLoggedInUser();
    if (!this.authService.hasUsableClientSession()) {
      return;
    }

    // Show the expiring-clients popup at most once per login session. Admin
    // shell mounts when the user enters /admin (post-login or after a hard
    // refresh), which is exactly the trigger we want. The previous design
    // listened on every NavigationEnd, which (a) hammered the API on each
    // route change when there were 0 expiring clients and (b) could re-open
    // the popup if the in-memory "shown" flag hadn't flipped yet. Both gone.
    this.checkExpiringClientsNotification();

    // Kick off the bell poll for super admins so login alerts surface.
    this.startNotificationPolling();

    // Watch for the auth interceptor flagging the session as expired so
    // we can render the inline banner without losing form state.
    this.sessionExpirySub = this.sessionExpiry.expired$.subscribe((flag) => {
      this.sessionExpiredBannerVisible = flag;
    });
  }

  /**
   * Re-login from the session-expired banner. We persist the URL the
   * user was on and route them through /auth/login; SessionExpiryService
   * has already stored the intended URL via markExpired().
   */
  reloginFromBanner(): void {
    this.sessionExpiry.reset();
    this.sessionExpiredBannerVisible = false;
    try {
      localStorage.removeItem("loggedInUser");
    } catch {
      /* ignore */
    }
    this.router.navigate(["/auth/login"]);
  }

  dismissSessionBanner(): void {
    this.sessionExpiredBannerVisible = false;
  }

  ngOnDestroy(): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
    if (this.notificationPollInterval) {
      clearInterval(this.notificationPollInterval);
      this.notificationPollInterval = null;
    }
    if (this.sessionExpirySub) {
      this.sessionExpirySub.unsubscribe();
      this.sessionExpirySub = null;
    }
  }

  /** True when the logged-in user has the SESSION_LOG_VIEW permission. */
  get canViewSessionLogs(): boolean {
    return this.authService.hasPermission("SESSION_LOG_VIEW");
  }

  /**
   * Convenience for the template - returns true for the privileged roles that
   * should see the login activity sidebar entry and bell-driven notifications.
   */
  get isPrivilegedAdmin(): boolean {
    const role = (this.loggedInUserRole || "").toString().toUpperCase();
    return (
      role === "SUPER ADMIN" || role === "SUPERADMIN" || this.canViewSessionLogs
    );
  }

  loadLoggedInUser(): void {
    const user = this.authService.getLoggedInUser();
    if (!user) {
      this.authService.clearClientAuthStateWithoutServerCall();
      void this.router.navigateByUrl("/welcome");
      return;
    }
    this.loggedInUserName = user.userName ?? null;
    this.loggedInUserImage = user.profilePic ?? null;
    this.loggedInUserRole = user.role ?? "Role";
    this.loggedInUserID = user._id ?? null;
  }

  logout(): void {
    this.authService
      .logout()
      .pipe(
        finalize(() => {
          this.authService.clearClientAuthStateWithoutServerCall();
          // Re-arm the per-login flag so a Dismiss / View / no-results
          // outcome doesn't leak across logout. The cross-day snooze
          // ("Don't display again today" - localStorage) is intentionally
          // NOT cleared here so it persists across logout / login until
          // end-of-day.
          try {
            sessionStorage.removeItem(
              AdminLayoutComponent.EXPIRING_POPUP_SHOWN_KEY,
            );
          } catch {
            /* storage may be disabled */
          }
          void this.router.navigateByUrl("/welcome");
        }),
      )
      .subscribe({ error: () => {} });
  }

  viewMyAccount(data: any): void {
    this.router.navigate([`admin/user-profile/${data}`]);
  }

  protected readonly drawer = {
    Components: [
      { name: "Button", icon: ICON },
      { name: "Input", icon: ICON },
      { name: "Tooltip", icon: ICON },
    ],
    Essentials: [
      { name: "Getting started", icon: ICON },
      { name: "Showcase", icon: ICON },
      { name: "Typography", icon: ICON },
    ],
  };

  protected handleToggle(): void {
    this.expanded.update((e) => !e);
  }


  //Dropdown

  navigateTo(index: number): void {
      switch(index) {
        case 0:
          this.router.navigate(['/admin/users']);
          break;
        case 1:
          this.router.navigate(['/admin/users']);
          break;
      }
    }

    view(): void {
      this.router.navigate([`admin/nurse/add-edit`]);
    }
  

    protected size: TuiSizeL | TuiSizeS = "s";
    protected openDropdown = false;

  /**
   * Two layers of suppression:
   *   - Per-login session flag in sessionStorage.
   *       Set by Dismiss, View expiring clients, and a successful "no rows"
   *       check. Survives reloads in the same tab; cleared on logout so
   *       the next login re-arms it.
   *   - Per-day snooze in localStorage.
   *       Set by "Don't display again today". Persists across logout /
   *       login on the same browser until end-of-day, by user request.
   * In checkExpiringClientsNotification we short-circuit if either is set.
   */
  private static readonly EXPIRING_POPUP_SHOWN_KEY =
    "expiringPopupShownThisSession";
  private static readonly EXPIRING_POPUP_SNOOZE_KEY =
    "expiringPopupSnoozeUntil";

  private hasShownExpiringPopupThisSession(): boolean {
    try {
      return (
        sessionStorage.getItem(AdminLayoutComponent.EXPIRING_POPUP_SHOWN_KEY) ===
        "1"
      );
    } catch {
      return false;
    }
  }

  private markExpiringPopupShown(): void {
    try {
      sessionStorage.setItem(
        AdminLayoutComponent.EXPIRING_POPUP_SHOWN_KEY,
        "1",
      );
    } catch {
      /* sessionStorage may be unavailable; popup will simply re-show on
         next admin-shell mount, which is acceptable degradation. */
    }
  }

  /**
   * True when the user has snoozed the expiring-clients popup for the
   * remainder of today (cross-login, persisted in localStorage).
   */
  private isExpiringPopupSnoozed(): boolean {
    try {
      const raw = localStorage.getItem(
        AdminLayoutComponent.EXPIRING_POPUP_SNOOZE_KEY,
      );
      if (!raw) return false;
      const until = parseInt(raw, 10);
      if (Number.isNaN(until)) return false;
      // Auto-purge expired snoozes so localStorage doesn't accrete state.
      if (Date.now() >= until) {
        try {
          localStorage.removeItem(
            AdminLayoutComponent.EXPIRING_POPUP_SNOOZE_KEY,
          );
        } catch {
          /* ignore */
        }
        return false;
      }
      return true;
    } catch {
      return false;
    }
  }

  checkExpiringClientsNotification(): void {
    const user = this.authService.getLoggedInUser();
    if (!user) return;

    // Per-login session short-circuit (Dismiss / View / no-results path).
    if (this.hasShownExpiringPopupThisSession()) return;

    // Cross-login day-long short-circuit ("Don't display again today").
    if (this.isExpiringPopupSnoozed()) return;

    // Show notification for SUPER ADMIN and SUPER MANAGER.
    if (user.role !== "SUPER ADMIN" && user.role !== "SUPER MANAGER") {
      return;
    }

    this.loadExpiringClients();
  }

  loadExpiringClients(): void {
    const payload = {
      pageIndex: 1,
      pageSize: 100, // Get all expiring clients
      filters: {
        searchText: "",
        status: null,
        archived: false,
      },
      sortField: "expiryDate",
      sortOrder: 1,
    };

    this.clientService.getPagedExpiredClients(payload).subscribe({
      next: (res: any) => {
        // If a dismiss or "don't show today" landed while the request was
        // in flight, respect it and don't reopen the dialog on response.
        if (
          this.hasShownExpiringPopupThisSession() ||
          this.isExpiringPopupSnoozed()
        ) {
          this.showExpiringClientsDialog = false;
          return;
        }
        if (res && res.length > 0 && res[0].data) {
          const currentDate = new Date();
          const sevenDaysFromNow = new Date();
          sevenDaysFromNow.setDate(sevenDaysFromNow.getDate() + 7);

          // Filter for active clients expiring within 7 days (not already expired)
          this.expiringClients = res[0].data.filter((client: any) => {
            if (client.expiryDate) {
              const expiryDate = new Date(client.expiryDate);
              return expiryDate >= currentDate && expiryDate <= sevenDaysFromNow;
            }
            return false;
          });

          this.expiringClientsCount = this.expiringClients.length;

          if (this.expiringClientsCount > 0) {
            this.showExpiringClientsDialog = true;
          }
        }

        // Whether or not we found any rows to show, mark the session as
        // "already checked" so we don't re-poll on later admin-shell
        // mounts (e.g. hard refresh) within the same login.
        this.markExpiringPopupShown();
      },
      error: (error) => {
        console.error("Error fetching expiring clients:", error);
        // On failure we deliberately do NOT mark the session as shown, so
        // the next admin-shell mount can retry. The bell/poll path is
        // unaffected.
      },
    });
  }

  /** Expiring list / popup — same formatted client id as everywhere else */
  formatExpiringClientId(client: any): string {
    return formatClientIdentifier(client);
  }

  startCountdown(): void {
    // Deprecated: dialog is dismissible immediately.
    this.countdownSeconds = 0;
  }

  closeExpiringClientsDialog(): void {
    this.showExpiringClientsDialog = false;
    this.markExpiringPopupShown();
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
  }

  /**
   * Hard "Don't show again today": writes a localStorage timestamp at
   * end-of-day and PERSISTS across logout / login on the same browser.
   * Also marks the per-session flag so we don't re-poll on the current
   * tab either.
   */
  snoozeExpiringClientsForToday(): void {
    try {
      const endOfDay = new Date();
      endOfDay.setHours(23, 59, 59, 999);
      localStorage.setItem(
        AdminLayoutComponent.EXPIRING_POPUP_SNOOZE_KEY,
        String(endOfDay.getTime()),
      );
    } catch {
      /* localStorage may be disabled; per-session flag below still
         suppresses the popup for this tab. */
    }
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
    this.showExpiringClientsDialog = false;
    this.markExpiringPopupShown();
  }

  navigateToExpiringClients(): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
    this.showExpiringClientsDialog = false;
    // Treat View as a dismiss too - the user has acknowledged the popup
    // and we shouldn't reopen it on the destination page's mount.
    this.markExpiringPopupShown();
    this.router.navigate(["/admin/client/expired"]);
  }

  /** Starts the notification bell poll for privileged admins. */
  private startNotificationPolling(): void {
    if (!this.isPrivilegedAdmin || !this.loggedInUserID) return;

    // Initial fetch + poll on a short interval so login alerts surface fast.
    this.fetchNotifications();
    this.notificationPollInterval = setInterval(
      () => this.fetchNotifications(),
      this.NOTIFICATION_POLL_MS,
    );
  }

  fetchNotifications(): void {
    if (!this.loggedInUserID) return;
    const payload = {
      pageIndex: 1,
      pageSize: 10,
      filters: { searchText: "" },
      sortField: "createdAt",
      sortOrder: -1,
    };
    this.notificationService
      .getPagedNotificationsByUserId(this.loggedInUserID, payload)
      .subscribe({
        next: (res: any) => {
          const data = Array.isArray(res) && res[0]?.data ? res[0].data : [];
          this.notifications = data;
          this.unopenedNotificationCount = data.filter(
            (n: any) => !n.isOpened,
          ).length;
        },
        error: () => {
          // Silent: bell is best-effort and must never block the admin shell.
        },
      });
  }

  toggleNotificationDropdown(): void {
    this.notificationDropdownOpen = !this.notificationDropdownOpen;
    if (this.notificationDropdownOpen) {
      this.fetchNotifications();
    }
  }

  openNotification(notification: any): void {
    if (!notification) return;
    if (!notification.isOpened) {
      this.notificationService
        .toggleNotificationOpenedStatus(notification._id)
        .subscribe({
          next: () => this.fetchNotifications(),
          error: () => {
            /* never block navigation on a stale opened flag */
          },
        });
    }
    this.notificationDropdownOpen = false;
    if (notification.link) {
      this.router.navigateByUrl(notification.link);
    }
  }

  markAllNotificationsRead(): void {
    if (!this.loggedInUserID) return;
    this.notificationService
      .markAllNotificationsAsOpened(this.loggedInUserID)
      .subscribe({
        next: () => this.fetchNotifications(),
        error: () => {
          /* swallow - bell will retry on next poll tick */
        },
      });
  }

  /**
   * Permanently dismiss a notification from the bell dropdown. Stops
   * propagation so the click doesn't also trigger openNotification's
   * navigation.
   */
  dismissNotification(notification: any, event?: Event): void {
    event?.stopPropagation();
    if (!notification?._id) return;
    this.notificationService.dismissNotification(notification._id).subscribe({
      next: () => {
        this.notifications = this.notifications.filter(
          (n) => n._id !== notification._id,
        );
        this.fetchNotifications();
      },
      error: () => {
        /* fall through; next poll will pick up the canonical state */
      },
    });
  }

  getDaysRemaining(expiryDate: string): number {
    const currentDate = new Date();
    const expiry = new Date(expiryDate);
    const timeDiff = expiry.getTime() - currentDate.getTime();
    const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
    return daysDiff;
  }
  
}
